import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Code,
  Combobox,
  Group,
  InputBase,
  Stack,
  Text,
  useCombobox,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconEye, IconFileUpload, IconPaperclip, IconX } from '@tabler/icons-react';
import {
  useAttachDocumentToEntry,
  useDetachDocumentFromEntry,
  useDocumentsList,
  useEntryAttachments,
  useFinalizeDocument,
  useRequestDownloadUrl,
  useRequestUploadUrl,
} from '../../queries/documents';
import { MAX_UPLOAD_SIZE_BYTES, putToR2, sha256Hex } from '../../lib/upload';
import { ApiError } from '../../lib/api-error';
import { useProjectStore } from '../../project/project-store';
import {
  DocumentPreviewModal,
  type PreviewableDoc,
} from '../documents/DocumentPreviewModal';

const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, 'image/tiff', 'image/webp'];
const MAX_SIZE = MAX_UPLOAD_SIZE_BYTES;

interface Props {
  entryId: string;
}

/**
 * Attachments block for a runsheet entry:
 *   - Lists already-attached documents
 *   - Lets the user attach from existing project documents (combobox)
 *   - Lets the user upload a fresh file which auto-attaches on finalize
 */
export function EntryAttachments({ entryId }: Props) {
  const projectRole = useProjectStore((s) => s.projectRole);
  const canWrite =
    projectRole === 'owner' || projectRole === 'moderator' || projectRole === 'editor';

  const { data: attachments = [], isLoading } = useEntryAttachments(entryId);
  const attachedIds = useMemo(() => new Set(attachments.map((a) => a.document_id)), [attachments]);

  const attachMut = useAttachDocumentToEntry();
  const detachMut = useDetachDocumentFromEntry();
  const download = useRequestDownloadUrl();
  const [preview, setPreview] = useState<PreviewableDoc | null>(null);

  return (
    <Stack gap="xs">
      {attachments.length === 0 && !isLoading && (
        <Text size="xs" c="dimmed">
          No documents attached yet.
        </Text>
      )}
      {attachments.map((a) => (
        <Group key={a.id} justify="space-between" wrap="nowrap">
          <Group
            gap="xs"
            wrap="nowrap"
            style={{ minWidth: 0, cursor: 'pointer' }}
            onClick={() =>
              setPreview({
                id: a.document_id,
                content_type: a.content_type ?? null,
                original_filename: a.original_filename ?? null,
                title: a.title ?? null,
              })
            }
          >
            <IconPaperclip size={14} />
            <Text size="sm" lineClamp={1}>
              {a.original_filename ?? a.title ?? a.document_id.slice(0, 8)}
            </Text>
            {a.content_type && (
              <Badge size="xs" variant="light">
                {a.content_type.split('/')[1]}
              </Badge>
            )}
          </Group>
          <Group gap={4} wrap="nowrap">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Open preview"
              onClick={() =>
                setPreview({
                  id: a.document_id,
                  content_type: a.content_type ?? null,
                  original_filename: a.original_filename ?? null,
                  title: a.title ?? null,
                })
              }
            >
              <IconEye size={14} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Download"
              onClick={async () => {
                try {
                  const res = await download.mutateAsync(a.document_id);
                  const link = document.createElement('a');
                  link.href = res.url;
                  if (res.filename) link.download = res.filename;
                  link.rel = 'noopener';
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                } catch (err) {
                  notifications.show({
                    title: 'Download failed',
                    message: err instanceof Error ? err.message : 'error',
                    color: 'red',
                  });
                }
              }}
            >
              <IconDownload size={14} />
            </ActionIcon>
            {canWrite && (
              <ActionIcon
                variant="subtle"
                size="sm"
                color="red"
                aria-label="Detach"
                onClick={() => detachMut.mutate({ id: a.id, entry_id: entryId })}
              >
                <IconX size={14} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      ))}

      <DocumentPreviewModal
        opened={preview !== null}
        onClose={() => setPreview(null)}
        doc={preview}
      />

      {canWrite && (
        <Stack gap="xs">
          <ExistingDocPicker
            entryId={entryId}
            excludeIds={attachedIds}
            onAttach={(docId) =>
              attachMut.mutateAsync({ entry_id: entryId, document_id: docId }).catch((err) => {
                notifications.show({
                  title: 'Attach failed',
                  message: err instanceof Error ? err.message : 'error',
                  color: 'red',
                });
              })
            }
          />
          <UploadAndAttachDropzone entryId={entryId} />
        </Stack>
      )}
    </Stack>
  );
}

/* ----------------------------- existing picker ---------------------------- */

interface PickerProps {
  entryId: string;
  excludeIds: Set<string>;
  onAttach: (documentId: string) => Promise<unknown>;
}

function ExistingDocPicker({ excludeIds, onAttach }: PickerProps) {
  const [search, setSearch] = useState('');
  const { data: docs = [] } = useDocumentsList({ q: search.trim() || undefined, limit: 20 });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = docs
    .filter((d) => !excludeIds.has(d.id) && !!d.sha256) // only finalized
    .map((d) => (
      <Combobox.Option value={d.id} key={d.id}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text size="sm" lineClamp={1}>
            {d.original_filename ?? d.title ?? d.id.slice(0, 8)}
          </Text>
          {d.content_type && (
            <Code fz="xs" c="dimmed">
              {d.content_type}
            </Code>
          )}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      withinPortal
      onOptionSubmit={async (val) => {
        await onAttach(val);
        combobox.closeDropdown();
        setSearch('');
      }}
    >
      <Combobox.Target>
        <InputBase
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          placeholder="Attach an existing document…"
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>No matches</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

/* ---------------------------- upload + attach ----------------------------- */

interface UploadAttachProps {
  entryId: string;
}

function UploadAndAttachDropzone({ entryId }: UploadAttachProps) {
  const requestUrl = useRequestUploadUrl();
  const finalize = useFinalizeDocument();
  const attach = useAttachDocumentToEntry();
  const [busy, setBusy] = useState<string | null>(null);

  async function handle(files: File[]) {
    for (const file of files) {
      setBusy(file.name);
      try {
        const sha = await sha256Hex(file);
        const pres = await requestUrl.mutateAsync({
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        });
        await putToR2({
          url: pres.upload.url,
          file,
          headers: pres.upload.headers,
        });
        await finalize.mutateAsync({ id: pres.document.id, sha256: sha });
        await attach.mutateAsync({ entry_id: entryId, document_id: pres.document.id });
        notifications.show({
          title: 'Attached',
          message: file.name,
          color: 'pine',
        });
      } catch (err) {
        notifications.show({
          title: 'Upload failed',
          message: err instanceof ApiError || err instanceof Error ? err.message : 'error',
          color: 'red',
        });
      } finally {
        setBusy(null);
      }
    }
  }

  return (
    <Dropzone
      onDrop={handle}
      accept={ACCEPT}
      maxSize={MAX_SIZE}
      loading={!!busy}
      multiple={false}
      p="sm"
    >
      <Group justify="center" gap="xs" mih={50}>
        <IconFileUpload size={18} />
        <Text size="xs">
          {busy ? `Uploading ${busy}…` : 'Or drop a new file to upload and attach'}
        </Text>
      </Group>
    </Dropzone>
  );
}
